/**
 * This is a ProductOffersCard component. It will be used to display all offers details to user with their specified url.
 *
 * @module views/components/ProductOffersCard
 * @memberof -Common
 */
import './ProductOffersCard.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Icon from '@ulta/core/components/Icon/Icon';
import Text from '@ulta/core/components/Text/Text';
import { hasItems } from '@ulta/core/utils/array/array';

import ProductOffersDetail from '../ProductOffersDetail/ProductOffersDetail';

/**
 * Represents a ProductOffersCard component
 *
 * @method
 * @param {ProductOffersCardProps} props - React properties passed from composition
 * @returns ProductOffersCard
 */
export const ProductOffersCard = function( props ){
  const {
    brandName,
    image,
    offers,
    offersAction,
    offersTitle,
    productName,
    variant = 'default',
    variantLabel,
    variantTypeLabel
  } = props;
  const offersActionLabel = offersAction?.accessibility?.label ?
    `${offersAction?.accessibility?.label} - ${offersAction.label}` :
    offersAction?.label;

  if( !hasItems( offers ) ){
    return null;
  }

  const sharedProps = {
    action: {
      ...offersAction,
      navigationType: 'flyout'
    },
    ariaLabel: offersActionLabel,
    alignment: 'right',
    crossButtonVisibility: true,
    overlayAriaLabel: offersTitle,
    overlayContent: (
      <ProductOffersDetail
        title={ offersTitle }
        offers={ offers }
        brandName={ brandName }
        productName={ productName }
        variantTypeLabel={ variantTypeLabel }
        variantLabel={ variantLabel }
        image={ image }
      />
    )
  };

  // If inline variant (seen in BagProductCardDetails in Cart) return button where words wrap and icon sits next to the last word.
  if( variant === 'inline' ){
    return (
      <div className='ProductOffersCard'>
        <Button
          { ...sharedProps }
          variant='navigation'
          className='ProductOffersCard__inline'
        >
          <Text textStyle='body-sm' htmlTag='span' color='magenta-500'>
            <span className='ProductOffersCard__inline--label'>
              { offersAction?.label }
            </span>

            <div className='ProductOffersCard__inline--icon'>
              <Icon name='ArrowForward' size='default' aria-hidden={ true } />
            </div>
          </Text>
        </Button>
      </div>
    );
  }

  // Return this default component
  return (
    <div className='ProductOffersCard'>
      <Button
        tertiary
        iconRight
        className={ classNames( 'ProductOffersCard_link', {
          ProductOffersCard_hasLabel: offersAction?.label
        } ) }
        iconImage='ArrowForward'
        iconSize='m'
        label={
          <div className='ProductOffersCard_label'>
            <Text textStyle='body-3' htmlTag='span' color='magenta-500'>
              { offersAction?.label }
            </Text>
          </div>
        }
        { ...sharedProps }
      />
    </div>
  );
};

/**
 * Property type definitions
 *
 * @typedef ProductOffersCardProps
 * @type {object}
 * @property {string} offersTitle - Sets the value of offersTitle
 * @property {object} offersAction - Sets the offersAction such as label and url for the product offers
 * @property {array} offers - List of offers for this product
 * @property {string} brandName - Sets the brandName
 * @property {string} productName - Sets the productName
 * @property {string} variant - Sets the variant
 * @property {string} variantTypeLabel - Sets the variantTypeLabel
 * @property {string} variantLabel - Sets the variantLabel
 * @property {object} image - Sets the image
 * @property {string} image.imageUrl - Sets the image
 */
export const propTypes = {
  offersTitle: PropTypes.string,
  offersAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string,
    accessibility: PropTypes.shape( {
      label: PropTypes.string
    } )
  } ).isRequired,
  offers: PropTypes.array,
  brandName: PropTypes.string,
  productName: PropTypes.string,
  variant: PropTypes.oneOf( ['inline', 'default'] ),
  variantTypeLabel: PropTypes.string,
  variantLabel: PropTypes.string,
  image: PropTypes.shape( {
    imageUrl: PropTypes.string
  } )
};

ProductOffersCard.propTypes = propTypes;

export default ProductOffersCard;
